import { useEffect } from 'react';
import { useSignageConfig } from '@/config/ConfigProvider';
import { warnOnce } from '@/lib/warnOnce';
import { useSignageStore } from '@/store/signageStore';
import { FlatLayout } from '@/widgets/FlatLayout';

/**
 * The config-driven renderer. Port of `moduleLoader.vue`.
 *
 * Reads `components` and `layout` from `useSignageConfig()` rather than from props: the
 * legacy prop drilling through `Home.vue` existed because Vue's `globalProperties` had to be
 * threaded down by hand, and React context is the direct equivalent of what that was working
 * around.
 */
export function ModuleLoader() {
  const config = useSignageConfig();

  /**
   * `moduleLoaded` — one third of the MQTT connect gate (docs/MIGRATION.md §5):
   * `preventerLoaded && moduleLoaded && topicItems.size > 0`.
   *
   * Empty deps, so it fires on mount and never again. Two things about the timing that look
   * like races and are not:
   *
   * 1. **It runs after the widgets have registered their topics.** React runs child effects
   *    before parent effects, and every widget's `useWidgetData` registration is a child
   *    effect of this one. So by the time this line executes, `topicItems` is already
   *    populated — no `setTimeout`, no `requestAnimationFrame`, no ordering hack.
   * 2. **StrictMode double-invokes it in dev.** `setModuleLoaded()` is an idempotent
   *    `set({ moduleLoaded: true })` — there is no toggle and no counter — so the second
   *    call writes the same value. Asserted in `ModuleLoader.test.tsx`, because SYM-489's
   *    connect gate hangs off this flag and a double-fire that flipped state twice would be
   *    a genuine bug.
   *
   * Read imperatively via `getState()`: subscribing to the action would re-render the whole
   * widget tree for nothing.
   */
  useEffect(() => {
    useSignageStore.getState().setModuleLoaded();
  }, []);

  /**
   * Slider seam — SYM-492.
   *
   * Warned from an effect rather than from render so StrictMode's double render does not
   * double-log; `warnOnce` would swallow the duplicate anyway, and this keeps the render
   * path free of side effects.
   *
   * None of the seven deployed configs carries a top-level `slider` block, so this path is
   * dead on every live display today. `szeged_2`, `szeged_3`, `szeged_4`, `corio`,
   * `freshwaves`, `hbr_tcc_2` and `hbr_tcc_3` do carry `slide: n` on some components, but
   * legacy `moduleLoader.vue` only reads `component.slide` inside `if (slider)` — with no
   * `slider` block those values are inert and every component renders in the flat layout.
   * That is the behaviour reproduced here.
   */
  useEffect(() => {
    if (!config.slider) return;
    warnOnce(
      'slider-not-implemented',
      '[signage] this config carries a `slider` block, which is not implemented yet ' +
        '(SYM-492); rendering every component in the flat layout instead.',
    );
  }, [config.slider]);

  // TODO(SYM-492): `if (config.slider) return <SliderLayout … />` goes here — slide-grouped
  // rendering with the `fade` / `right-to-left` effects and the progress bar.
  return <FlatLayout components={config.components} spacing={config.layout.components} />;
}
